/**
 * Shapes of the JSON API v1 (app/ARCHITECTURE.md) and their runtime
 * validation: every answer is checked before the code trusts its type.
 */

import { InvalidResponseError, isRecord } from "../lib/http";

export type SubscriptionState =
  "active" | "canceled" | "contact_support" | "unknown";

export interface Account {
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly subscription: SubscriptionState;
}

export interface Limits {
  readonly maxUploadBytes: number;
  readonly maxCombineFiles: number;
}

export interface Me {
  readonly premium: boolean;
  readonly account: Account | null;
  readonly limits: Limits;
}

export interface Upload {
  readonly id: string;
  readonly name: string;
  readonly size: number;
  readonly extension: string;
}

export interface Download {
  readonly id: string;
  readonly name: string;
  readonly size: number;
  readonly createdAt: Date;
  readonly url: string;
}

export type JobStatus = "queued" | "running" | "succeeded" | "failed";

export interface JobError {
  readonly code: string;
  readonly message: string;
}

export interface Job {
  readonly id: string;
  readonly tool: string;
  readonly status: JobStatus;
  readonly progress: number;
  readonly error: JobError | null;
  readonly results: readonly Download[];
}

const SUBSCRIPTION_STATES: ReadonlySet<string> = new Set([
  "active",
  "canceled",
  "contact_support",
  "unknown",
]);
const JOB_STATUSES: ReadonlySet<string> = new Set([
  "queued",
  "running",
  "succeeded",
  "failed",
]);
// downloads are served by the app itself: /download/<id>
const DOWNLOAD_URL_RE = /^\/download\/[A-Za-z0-9_-]+$/;

function fail(what: string): never {
  throw new InvalidResponseError(`Unexpected answer from the server (${what})`);
}

function object(value: unknown, what: string): Record<string, unknown> {
  return isRecord(value) ? value : fail(what);
}

function string(data: Record<string, unknown>, key: string): string {
  const value = data[key];
  return typeof value === "string" ? value : fail(key);
}

function number(data: Record<string, unknown>, key: string): number {
  const value = data[key];
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : fail(key);
}

function boolean(data: Record<string, unknown>, key: string): boolean {
  const value = data[key];
  return typeof value === "boolean" ? value : fail(key);
}

function isSubscriptionState(value: string): value is SubscriptionState {
  return SUBSCRIPTION_STATES.has(value);
}

function isJobStatus(value: string): value is JobStatus {
  return JOB_STATUSES.has(value);
}

export function parseSubscription(value: unknown): SubscriptionState {
  return typeof value === "string" && isSubscriptionState(value)
    ? value
    : fail("subscription");
}

function parseAccount(value: unknown): Account {
  const data = object(value, "account");
  return {
    email: string(data, "email"),
    firstName: string(data, "first_name"),
    lastName: string(data, "last_name"),
    subscription: parseSubscription(data["subscription"]),
  };
}

export function parseMe(value: unknown): Me {
  const data = object(value, "me");
  const limits = object(data["limits"], "limits");
  return {
    premium: boolean(data, "premium"),
    account: data["account"] === null ? null : parseAccount(data["account"]),
    limits: {
      maxUploadBytes: number(limits, "max_upload_bytes"),
      maxCombineFiles: number(limits, "max_combine_files"),
    },
  };
}

export function parseUpload(value: unknown): Upload {
  const data = object(object(value, "upload answer")["upload"], "upload");
  return {
    id: string(data, "id"),
    name: string(data, "name"),
    size: number(data, "size"),
    extension: string(data, "extension"),
  };
}

export function parseDownload(value: unknown): Download {
  const data = object(value, "download");
  const id = string(data, "id");
  const createdAt = new Date(string(data, "created_at"));
  if (Number.isNaN(createdAt.getTime())) fail("created_at");
  const url = string(data, "url");
  return {
    id,
    name: string(data, "name"),
    size: number(data, "size"),
    createdAt,
    url: DOWNLOAD_URL_RE.test(url)
      ? url
      : `/download/${encodeURIComponent(id)}`,
  };
}

export function parseDownloads(value: unknown): Download[] {
  const list = object(value, "downloads")["downloads"];
  return Array.isArray(list) ? list.map(parseDownload) : fail("downloads");
}

export function parseJob(value: unknown): Job {
  const data = object(object(value, "job answer")["job"], "job");
  const status = string(data, "status");
  const error = data["error"];
  const results = data["results"];
  return {
    id: string(data, "id"),
    tool: string(data, "tool"),
    status: isJobStatus(status) ? status : fail("status"),
    progress: Math.min(100, Math.max(0, number(data, "progress"))),
    error:
      error === null || error === undefined
        ? null
        : {
            code: string(object(error, "error"), "code"),
            message: string(object(error, "error"), "message"),
          },
    results: Array.isArray(results) ? results.map(parseDownload) : [],
  };
}
